namespace Solovis.Reports.Parameters.Stats {

  export interface StatSetOptions {
    defaultBenchmark?: StatSetOptionsDefaultBenchmark;
    riskFreeRateRef?: StatSetOptionsBenchmarkRef;
    annualizedRiskFreeRate?: boolean;
    annualizationMethod?: string;

    values?: StatSetOptionsValues;
    basicStats?: StatSetOptionsBasicStats;
    benchmarkedBasicStats?: StatSetOptionsBenchmarkedBasicStats;
    basicStatsBenchmarkRef?: StatSetOptionsBenchmarkRef;

    peaksAndTroughs?: StatSetOptionsPeaksAndTroughs;

    gainLossSummary?: boolean;
    betaAlpha?: StatSetOptionsBetaAlpha;
    sortinoRatio?: StatSetOptionsSortinoRatio;
    sharpeRatio?: StatSetOptionsSharpeRatio;
    informationRatio?: StatSetOptionsInformationRatio;
    upsideDownsideCaptureRatio?: StatSetOptionsUpsideDownsideCaptureRatio;

    pme?: StatSetOptionsPme;

    composite?: StatSetOptionsComposite;

    tx?: StatSetOptionsTx;

    drawdown?: StatSetOptionsDrawdown;

    sequences?: SequenceOptions[];
  }

  export interface StatSetOptionsDefaultBenchmark {
    source?: string;
  }

  export interface StatSetOptionsValues {
    endValue?: boolean;
    startValue?: boolean;
    netValueGainLoss?: boolean;
    income?: boolean;
    localValues?: boolean;

    units?: boolean;
    unitPrice?: boolean;
    contractSize?: boolean;
    ownershipPercentage?: boolean;
    averageBalance?: boolean;

    lastValueAboveValueTypeThresholdId?: number;

    accruedIncomeStartValue?: boolean;
    accruedIncomeEndValue?: boolean;

    twrVariations?: string[];
  }

  export interface StatSetOptionsBasicStats {
    cumulativeReturn?: boolean;
    annualizedReturn?: boolean;

    twrVariations?: string[];

    standardDeviation?: boolean;
    annualizedStandardDeviation?: boolean;
    variance?: boolean;
    downsideStandardDeviation?: boolean;
    mean?: boolean;
    sampleSkewness?: boolean;
    sampleExcessKurtosis?: boolean;

    annualizedIrr?: boolean;
    cumulativeIrr?: boolean;

    hidePartialPeriodReturns?: boolean;

    irrVariations?: string[];

    endOfDayAnnualizedIrr?: boolean;
    endOfDayCumulativeIrr?: boolean;
  }

  export interface StatSetOptionsBenchmarkedBasicStats {
    correlation?: boolean;
    covariance?: boolean;
    deltaMean?: boolean;
    deltaStandardDeviation?: boolean;
    downsideDeltaStandardDeviation?: boolean;
  }

  export type SequenceType = "Month" | "CalendarQuarter" | "FloatingQuarter" | "CalendarYear";
  export type ReturnModeOption = "ChainedMonthly" | "Irr";

  export interface SequenceOptions {
    label?: string;
    code?: string;
    type?: SequenceType;
    returnMode?: ReturnModeOption;
    annualizationMethod?: string;
    perfVariationCode?: string;

    basicStats?: StatSetOptionsBasicStats;
    benchmarkedBasicStats?: StatSetOptionsBenchmarkedBasicStats;
    gainLossSummary?: boolean;
    betaAlpha?: StatSetOptionsBetaAlpha;
    informationRatio?: StatSetOptionsInformationRatio;
  }

  export interface StatSetOptionsBenchmarkRef {
    useNone?: boolean;
    resourceId?: number;
    useRiskFreeRate?: boolean;
    useDefaultBenchmark?: boolean;

    fixedAnnualRate?: number | string;
    fixedRateCurrencyId?: number;
  }

  export interface StatSetOptionsBetaAlpha {
    marketRef?: StatSetOptionsBenchmarkRef;

    annualizedAlpha?: boolean;
    annualizedTreynorRatio?: boolean;
  }

  export interface StatSetOptionsSortinoRatio {
    targetReturnRef?: StatSetOptionsBenchmarkRef;
  }

  export interface StatSetOptionsSharpeRatio {
    benchmarkRef?: StatSetOptionsBenchmarkRef;
  }

  export interface StatSetOptionsInformationRatio {
    benchmarkRef?: StatSetOptionsBenchmarkRef;
  }

  export interface StatSetOptionsUpsideDownsideCaptureRatio {
    benchmarkRef?: StatSetOptionsBenchmarkRef;
  }

  export interface StatSetOptionsPeaksAndTroughs {
    maxDrawdown?: boolean;
    sincePeakPerformance?: boolean;
    useQuarterEndsForPeaksAndTroughs?: boolean;
  }

  export interface StatSetOptionsPme {
    benchmarkRef?: StatSetOptionsBenchmarkRef;
    longNickelsPme?: boolean;
    longNickelsPmeGainLoss?: boolean;
    pmePlus?: boolean;
    modifiedPme?: boolean;
    kaplanSchoarPme?: boolean;
    directAlpha?: boolean;
    excessIrr?: boolean;
  }

  export interface StatSetOptionsComposite {
    contributionToReturn?: boolean;
    percentOfAll?: boolean;
    attribution?: StatSetOptionsAttribution;
  }

  export interface StatSetOptionsAttribution {
    benchmarkEntityIds?: number[];

    style?: "Arithmetic" | "Geometric";
    interactionEffect?: "WithSelectionEffect" | "WithAllocationEffect" | "Standalone";

    includeLineItemBenchmarkEffect?: boolean;
  }

  export interface StatSetOptionsTx {
    netAmountIn?: boolean;
    amountIn?: boolean;
    amountOut?: boolean;
    dietzWeightedIn?: boolean;
    localValues?: boolean;
    netIncomeIn?: boolean;

    customTxGroups?: StatSetOptionsCustomTxGroup[];

    usePartialPeriodLogic?: boolean;
    startDateResourceId?: number;
    endDateResourceId?: number;
  }

  export interface StatSetOptionsCustomTxGroup {
    label?: string;
    code?: string;

    performanceVariationCode?: string;

    startOfDay?: boolean;
    endOfDay?: boolean;

    forceStartOfDay?: boolean;
    forceEndOfDay?: boolean;

    txTypeFilter?: Solovis.Reports.Parts.Tables.DataTableHelpers.FilterByTagsMatch;
  }

  export interface StatSetOptionsDrawdown {
    start?: boolean;
    end?: boolean;
    local?: boolean;

    commitment?: boolean;
    uncalledCapital?: boolean;
    recallable?: boolean;

    tvpi?: boolean;
    dpi?: boolean;

    capitalCalledSinceInception?: boolean;
    distributionsSinceInception?: boolean;
  }


  export class StatSetParameterControl extends Solovis.UI.BaseControl {

    public static hasNewRender = true;

    private _statSetOptions;

    private _statSetLabel;
    private _statSetEditLink;

    constructor(options) {
      super(options);

      this._statSetOptions = (typeof options.initialValue != "undefined") ? options.initialValue : null;
    }

    public doRender() {
      if (this.options.renderMode === 'modern') {
        this.doRenderNew();
      } else {
        this.doRenderClassic();
      }
    }

    public async doRenderNew() {
      await Solovis.UX.Utils.placeComponent("ConfigureControl", this.container[0], { onClick: (event) => { this._showStatSetEditor() } })
    }

    public doRenderClassic() {
      let self = this;

      this._statSetLabel = $("<a>").text("Statistics Set").appendTo(this.container);
      this._statSetLabel.click(function () {
        self._showStatSetEditor();
      });

      this._statSetEditLink = $("<a>").appendTo(this.container)
        .css("padding-left", "5px")
        .html("<span class='fa fa-pencil'></span>");
      this._statSetEditLink.click(function () {
        self._showStatSetEditor();
      });
    }


    private _showStatSetEditor() {
      let editorDialog = new StatSetEditorDialog({
        statSetOptions: this._statSetOptions,
        saveCallback: (newStatSetOptions) => {
          this.setParameterValue(newStatSetOptions);
        }
      });

      editorDialog.show();
    }


    public _setCurrentStatSetOptions(newInteralSetOptions) {
      this._statSetOptions = newInteralSetOptions;
    }

    public setParameterValue(newValue, more?) {
      Solovis.Reports.Parameters.ParameterControlHelper.handleSetParameterValue(
        this, StatSetParameterControl, this._setCurrentStatSetOptions, newValue, more);
    }

    public getParameterValue() {
      return Solovis.deepCopy(this._statSetOptions);
    }

    public notifyParameterValueChanged() {
      $(this).trigger("parameterValueChanged");
    }
  }


  export class StatSetEditorDialog {
    public static _sequenceBasicStatsFieldList = [
      "cumulativeReturn",
      "annualizedReturn",
      "standardDeviation",
      "annualizedStandardDeviation",
      "variance",
      "downsideStandardDeviation",
      "mean",
      "sampleSkewness",
      "sampleExcessKurtosis"
    ];

    public static _sequenceStatsFieldLabels = {
      "cumulativeReturn": "Cumulative Return",
      "annualizedReturn": "Annualized Return",
      "standardDeviation": "Standard Deviation",
      "annualizedStandardDeviation": "Annualized Standard Deviation",
      "variance": "Variance",
      "downsideStandardDeviation": "Downside Standard Deviation",
      "mean": "Arithmetic Mean",
      "sampleSkewness": "Sample Skewness",
      "sampleExcessKurtosis": "Sample Excess Kurtosis",

      "correlation": "Correlation",
      "covariance": "Covariance",
      "deltaMean": "Delta Mean",
      "deltaStandardDeviation": "Delta Standard Deviation",
      "downsideDeltaStandardDeviation": "Downside Delta Standard Deviation"
    };

    public static _coreStatsFieldList = [
      "cumulativeReturn",
      "annualizedReturn",
      "cumulativeIrr",
      "annualizedIrr",
      "standardDeviation",
      "annualizedStandardDeviation",
      "variance",
      "downsideStandardDeviation",
      "mean",
      "sampleSkewness",
      "sampleExcessKurtosis"
    ];

    public static _benchmarkedStatsFieldList = [
      "correlation",
      "covariance",
      "deltaMean",
      "deltaStandardDeviation",
      "downsideDeltaStandardDeviation"
    ];

    public static _statsFieldLabels = {
      "cumulativeReturn": "Cumulative Return",
      "annualizedReturn": "Annualized Return",
      "standardDeviation": "Monthly Standard Deviation",
      "annualizedStandardDeviation": "Annualized Standard Deviation",
      "variance": "Monthly Variance",
      "downsideStandardDeviation": "Monthly Downside Standard Deviation",

      "cumulativeIrr": "Cumulative IRR",
      "annualizedIrr": "Annualized IRR",

      "mean": "Monthly Arithmetic Mean",
      "sampleSkewness": "Monthly Sample Skewness",
      "sampleExcessKurtosis": "Monthly Sample Excess Kurtosis",
      "correlation": "Monthly Correlation",
      "covariance": "Monthly Covariance",
      "deltaMean": "Monthly Delta Mean",
      "deltaStandardDeviation": "Monthly Delta Standard Deviation",
      "downsideDeltaStandardDeviation": "Monthly Downside Delta Standard Deviation"
    };

    public static _statsFieldHelp = {
      "cumulativeReturn": { url: "~/Help/InvestmentModelHelp/CalculationsReference#cumulative_return" },
      "annualizedReturn": { url: "~/Help/InvestmentModelHelp/CalculationsReference#annualized_return" },
      "standardDeviation": { url: "~/Help/InvestmentModelHelp/CalculationsReference#monthly_standard_deviation" },
      "annualizedStandardDeviation": { url: "~/Help/InvestmentModelHelp/CalculationsReference#annualized_standard_deviation" },
      "variance": { url: "~/Help/InvestmentModelHelp/CalculationsReference#monthly_variance" },
      "downsideStandardDeviation": { url: "~/Help/InvestmentModelHelp/CalculationsReference#monthly_downside_standard_deviation" },

      "cumulativeIrr": { url: "~/Help/InvestmentModelHelp/CalculationsReference#cumulative_irr" },
      "annualizedIrr": { url: "~/Help/InvestmentModelHelp/CalculationsReference#annualized_irr" },

      "mean": { url: "~/Help/InvestmentModelHelp/CalculationsReference#monthly_arithmetic_mean" },
      "sampleSkewness": { url: "~/Help/InvestmentModelHelp/CalculationsReference#monthly_sample_skewness" },
      "sampleExcessKurtosis": { url: "~/Help/InvestmentModelHelp/CalculationsReference#monthly_sample_excess_kurtosis" },
      "correlation": { url: "~/Help/InvestmentModelHelp/CalculationsReference#monthly_correlation" },
      "covariance": { url: "~/Help/InvestmentModelHelp/CalculationsReference#monthly_covariance" },

      "deltaMean": { url: "~/Help/InvestmentModelHelp/CalculationsReference#monthly_delta_mean" },
      "deltaStandardDeviation": { url: "~/Help/InvestmentModelHelp/CalculationsReference#monthly_delta_standard_deviation" },
      "downsideDeltaStandardDeviation": { url: "~/Help/InvestmentModelHelp/CalculationsReference#monthly_downside_delta_standard_deviation" },
    };

    private _statSetOptions: StatSetOptions;
    private _saveCallback;

    private _formBuilder: Solovis.UI.InputControls.FormBuilder;
    private _dialog: Solovis.UI.Dialog;
    private _modelEnvSettings;
    private _propertyList;

    private twrVariationsOptionsList: any[] = new Array();
    private irrVariationsOptionsList: any[] = new Array();

    public constructor(options) {
      this._statSetOptions = options.statSetOptions ? options.statSetOptions : {};
      this._saveCallback = options.saveCallback;

      let portalServices = new Solovis.Portal.SitePortalServices();
    }

    public async show() {
      let statSetOptions = this._statSetOptions;

      const modelEnvSettingsResponse = await (Solovis as any).Reports.ReportDataLoader.loadDataSetAsync(
        "/Solovis/Metadata/ModelEnvironmentSettings", {}, null, { invokeMethod: "invokeAndWait" }
      );
      this._modelEnvSettings = modelEnvSettingsResponse.result.dataSet.modelEnvironmentSettingsInfo;


      let formBuilderDiv = $("<div>");
      let formBuilder = new Solovis.UI.InputControls.FormBuilder({
        container: formBuilderDiv,
        width: 800,
        labelColumnWidth: 245
      });
      this._formBuilder = formBuilder;

      let customEnvironmentUserData = formBuilder.portalServices().customEnvironmentUserData();

      // Determine variations lists.
      if (customEnvironmentUserData && customEnvironmentUserData.ModelSettings) {
        let modelSettings = customEnvironmentUserData.ModelSettings;
        if (modelSettings && modelSettings.performanceVariationsConfig && modelSettings.performanceVariationsConfig.variations) {
          for (let variation of modelSettings.performanceVariationsConfig.variations) {
            if (variation.allowForTwr) {
              this.twrVariationsOptionsList.push({ text: variation.label, value: variation.code });
            }
            if (variation.allowForIrr) {
              this.irrVariationsOptionsList.push({ text: variation.label, value: variation.code });
            }
          }
        }
      }

      this._addBenchmarkRefEntry("riskFreeRateRef", "Risk-Free Rate", statSetOptions.riskFreeRateRef, { isRiskFreeRate: true });

      this._addValues();
      this._addBasicStats();
      this._addSequenceStats();
      this._addPeaksAndTroughs();
      this._addBetaAlpha();
      this._addSortinoRatio();
      this._addSharpeRatio();
      this._addInformationRatio();
      this._addUpsideDownsideCaptureRatio();
      this._addPme();
      this._addComposite();
      this._addTx();
      this._addDrawdown();
      this._addAdvancedSection();

      formBuilder.setupDone();

      formBuilder.showAsDialog({
        title: "Edit Statistics Set",

        desiredWidth: 800,
        desiredHeight: 1000,

        showSaveCancel: true,
        saveButtonText: "OK",

        saveCallback: () => {
          let newOptions = this._getNewStatSetOptions();

          this._saveCallback(newOptions);

          return true;
        }
      });
    }

    private _addBenchmarkRefEntry(
      name,
      label,
      initialValue,
      benchmarkOptions?: {
        isRiskFreeRate?: boolean,
        noRiskFreeRate?: boolean,
        noFixedRate?: boolean,
        isVisibleParent?: string
      }
    ) {
      this._formBuilder.addEntry({
        name: name,
        label: label,

        initialValue: initialValue,

        createEditor: (createEditorOptions) => {
          let benchmarkControl = new StatSetBenchmarkRefControl({
            controlHost: createEditorOptions.container,
            benchmarkRef: createEditorOptions.initialValue,
            isRiskFreeRate: benchmarkOptions && benchmarkOptions.isRiskFreeRate,
            noRiskFreeRate: benchmarkOptions && benchmarkOptions.noRiskFreeRate,
            noFixedRate: benchmarkOptions && benchmarkOptions.noFixedRate
          });
          benchmarkControl.setAsCurrentControl();

          let editor = {
            getValue: () => {
              return benchmarkControl.getBenchmarkRef();
            }
          };

          return editor;
        },

        isVisible: (entry) => {
          if (!benchmarkOptions || !benchmarkOptions.isVisibleParent) {
            return true;
          }
          else if (entry.formBuilder.getValueByName(benchmarkOptions.isVisibleParent)) {
            return true;
          }
          else {
            return false;
          }
        }
      });
    }


    private _addValues() {
      let statSetOptions = this._statSetOptions;
      let values: StatSetOptionsValues = statSetOptions.values ? statSetOptions.values : {};
      let valuesPreviouslySet = !!statSetOptions.values;

      let formBuilder = this._formBuilder;

      formBuilder.addEntry({
        isGroupHeader: true,
        name: "valuesGroup",
        label: "Values"
      });

      formBuilder.addEntry({
        name: "values",
        label: "Values",

        initialValue: valuesPreviouslySet,

        editor: {
          type: "bool"
        }
      });

      formBuilder.addEntry({
        name: "values.endValue",
        label: "End Value",

        initialValue: valuesPreviouslySet ? values.endValue : true,

        editor: {
          type: "bool"
        },

        isVisible: (entry) => { return !!this._formBuilder.getValueByName("values"); }
      });

      formBuilder.addEntry({
        name: "values.startValue",
        label: "Start Value",

        initialValue: values.startValue,

        editor: {
          type: "bool"
        },

        isVisible: (entry) => { return !!this._formBuilder.getValueByName("values"); }
      });

      formBuilder.addEntry({
        name: "values.units",
        label: "Units",

        initialValue: values.units,

        editor: {
          type: "bool"
        },

        isVisible: (entry) => { return !!this._formBuilder.getValueByName("values"); }
      });

      formBuilder.addEntry({
        name: "values.unitPrice",
        label: "Unit Price",

        initialValue: values.unitPrice,

        editor: {
          type: "bool"
        },

        isVisible: (entry) => { return !!this._formBuilder.getValueByName("values"); }
      });

      formBuilder.addEntry({
        name: "values.contractSize",
        label: "Contract Size",

        initialValue: values.contractSize,

        editor: {
          type: "bool"
        },

        isVisible: (entry) => { return !!this._formBuilder.getValueByName("values"); }
      });

      formBuilder.addEntry({
        name: "values.ownershipPercentage",
        label: "Ownership Percentage",

        help: "For most assets, equal to Investment Value / Holding Entity Value. For drawdown funds, equal to Investment Commitment / Holding Entity Commitment.",

        initialValue: values.ownershipPercentage,

        editor: {
          type: "bool"
        },

        isVisible: (entry) => { return !!this._formBuilder.getValueByName("values"); }
      });

      formBuilder.addEntry({
        name: "values.averageBalance",
        label: "Average Balance",

        help: "Equal to the start value plus the net Dietz-weighted transaction amount.",

        initialValue: values.averageBalance,

        editor: {
          type: "bool"
        },

        isVisible: (entry) => { return !!this._formBuilder.getValueByName("values"); }
      });

      formBuilder.addEntry({
        name: "values.netValueGainLoss",
        label: "Net Value Gain/Loss",

        help: "Equal to End Value - Start Value - Net Amount In.",

        initialValue: values.netValueGainLoss,

        editor: {
          type: "bool"
        },

        isVisible: (entry) => { return !!this._formBuilder.getValueByName("values"); }
      });

      formBuilder.addEntry({
        name: "values.income",
        label: "Income",

        help: { url: "~/Help/InvestmentModelHelp/CalculationsReference#income" },

        initialValue: values.income,

        editor: {
          type: "bool"
        },

        isVisible: (entry) => { return this._modelEnvSettings.enableAccruedIncome && !!this._formBuilder.getValueByName("values"); }
      });

      formBuilder.addEntry({
        name: "values.localValues",
        label: "Local Values",

        help: "Includes values in an asset's local currency as well as the target currency.",
        initialValue: values.localValues,

        editor: {
          type: "bool"
        },

        isVisible: (entry) => { return !!this._formBuilder.getValueByName("values"); }
      });

      formBuilder.addEntry({
        name: "values.lastValueAboveValueTypeThresholdId",
        label: "Last Value Above Value Type Threshold",

        help: "If set, include values and date for the most recent date (within one year) where the value type is equal to or greater than the chosen value type." 
         + " For instance, choosing Actual will show the most recent user- entered Actual value and its date.",

        initialValue: values.lastValueAboveValueTypeThresholdId,

        editor: {
          type: "parameter",
          parameterOptions: {
            parameterType: "valueType",
            settings: {
              allowNull: true
            }
          }
        },

        isVisible: (entry) => { return !!this._formBuilder.getValueByName("values"); }
      });

      formBuilder.addEntry({
        name: "values.accruedIncomeStartValue",
        label: "Accrued Income Starting Balance",

        initialValue: values.accruedIncomeStartValue,

        editor: {
          type: "bool"
        },

        isVisible: (entry) => { return this._modelEnvSettings.enableAccruedIncome && !!this._formBuilder.getValueByName("values"); }
      });

      formBuilder.addEntry({
        name: "values.accruedIncomeEndValue",
        label: "Accrued Income Ending Balance",

        initialValue: values.accruedIncomeEndValue,

        editor: {
          type: "bool"
        },

        isVisible: (entry) => { return this._modelEnvSettings.enableAccruedIncome && !!this._formBuilder.getValueByName("values"); }
      });

      formBuilder.addEntry({
        name: "values.twrVariations",
        label: "TWR Variations",
        help: "Includes additional values and gain/loss statistics if these are affected by the chosen TWR variation as defined in the admin site settings.",
        initialValue: values.twrVariations ? values.twrVariations : null,

        createEditor: (entryOptions) => {
          $(entryOptions.container).dxTagBox({
            dataSource: this.twrVariationsOptionsList,
            displayExpr: "text",
            valueExpr: "value",
            value: entryOptions.initialValue
          });

          let editor = {
            getValue: () => {
              let newValue = $(entryOptions.container).dxTagBox("instance").option("selectedItems");
              if (newValue) {
                let newVariationCodes = _.map(newValue, (item: any) => { return item.value; });
                return newVariationCodes;
              }
              return null;
            }
          };

          return editor;
        },

        isVisible: (entry) => { return !!this._formBuilder.getValueByName("values") && !_.isEmpty(this.twrVariationsOptionsList); }
      });
    }

    private _addBasicStats() {
      let statSetOptions = this._statSetOptions;
      let basicStats: StatSetOptionsBasicStats = statSetOptions.basicStats ? statSetOptions.basicStats : {};
      let benchmarkedBasicStats: StatSetOptionsBenchmarkedBasicStats = statSetOptions.benchmarkedBasicStats ? statSetOptions.benchmarkedBasicStats : {};

      let formBuilder = this._formBuilder;

      // Basic Stats
      formBuilder.addEntry({
        isGroupHeader: true,
        name: "basicStats",
        label: "Basic Stats"
      });

      for (let statsField of StatSetEditorDialog._coreStatsFieldList) {
        let label = StatSetEditorDialog._statsFieldLabels[statsField];
        if (!label) {
          label = statsField;
        }

        formBuilder.addEntry({
          name: statsField,
          label: label,

          help: StatSetEditorDialog._statsFieldHelp[statsField],

          initialValue: basicStats[statsField],

          editor: {
            type: "bool"
          }
        });

        if (statsField === "annualizedReturn") {
          // This is where we slot in TWR Variations (after "Annualized Return" option).
          formBuilder.addEntry({
            name: "twrVariations",
            label: "TWR Variations",

            help: "Includes additional performance variations of the cumulative/annualized TWR if defined in the admin Site Settings.",
            
            initialValue: basicStats.twrVariations ? basicStats.twrVariations : null,

            createEditor: (entryOptions) => {
              $(entryOptions.container).dxTagBox({
                dataSource: this.twrVariationsOptionsList,
                displayExpr: "text",
                valueExpr: "value",

                value: entryOptions.initialValue
              });

              let editor = {
                getValue: () => {
                  let newValue = $(entryOptions.container).dxTagBox("instance").option("selectedItems");
                  if (newValue) {
                    let newVariationCodes = _.map(newValue, (item: any) => { return item.value; });
                    return newVariationCodes;
                  }

                  return null;
                }
              };

              return editor;
            },

            isVisible: (entry) => { return !_.isEmpty(this.twrVariationsOptionsList); }
          });
        } else if (statsField === "annualizedIrr") {
          // Adding IRR Variations field after the Annualized IRR field
          formBuilder.addEntry({
            name: "irrVariations",
            label: "IRR Variations",

            help: "Includes additional performance variations of the cumulative/annualized IRR if defined in the admin Site Settings.",

            initialValue: basicStats.irrVariations ? basicStats.irrVariations : null,

            createEditor: (entryOptions) => {
              $(entryOptions.container).dxTagBox({
                dataSource: this.irrVariationsOptionsList,
                displayExpr: "text",
                valueExpr: "value",

                value: entryOptions.initialValue
              });

              let editor = {
                getValue: () => {
                  let newValue = $(entryOptions.container).dxTagBox("instance").option("selectedItems");
                  if (newValue) {
                    let newVariationCodes = _.map(newValue, (item: any) => { return item.value; });
                    return newVariationCodes;
                  }

                  return null;
                }
              };

              return editor;
            },

            isVisible: (entry) => { return !_.isEmpty(this.irrVariationsOptionsList); }
          });

          let annualizationOptions = Solovis.Reports.Parameters.AnnualizationMethodParameterControl.getAnnualizationOptions();
          let modelEnvdefaultAnnualizationMethod = _.find(annualizationOptions, option => option.value === this._modelEnvSettings.defaultAnnualizationMethod)
          let defaultOption = { text: `Default - ${modelEnvdefaultAnnualizationMethod.text}`, value: "" }

          formBuilder.addEntry({
            name: "annualizationMethod",
            label: "Annualization Method",

            initialValue: statSetOptions.annualizationMethod
              ? statSetOptions.annualizationMethod
              : defaultOption.value,

            editor: {
              type: "select",
              selectOptions: {
                data: [defaultOption].concat(annualizationOptions)
              }
            }
          });
        }
      }

      formBuilder.addEntry({
        name: "gainLossSummary",
        label: "Monthly Gain/Loss Summary",

        help: { url: "~/Help/InvestmentModelHelp/CalculationsReference#monthly_gain_loss_summary" },

        initialValue: statSetOptions.gainLossSummary,

        editor: {
          type: "bool"
        }
      });

      // Benchmarked Basic Stats
      formBuilder.addEntry({
        isGroupHeader: true,
        name: "benchmarkedBasicStats",
        label: "Benchmarked Basic Stats"
      });

      this._addBenchmarkRefEntry(
        "basicStatsBenchmarkRef",
        "Basic Stats Benchmark",
        statSetOptions.basicStatsBenchmarkRef ? statSetOptions.basicStatsBenchmarkRef : { useNone: true });

      for (let statsField of StatSetEditorDialog._benchmarkedStatsFieldList) {
        let label = StatSetEditorDialog._statsFieldLabels[statsField];
        if (!label) {
          label = statsField;
        }

        formBuilder.addEntry({
          name: statsField,
          label: label,

          help: StatSetEditorDialog._statsFieldHelp[statsField],

          initialValue: benchmarkedBasicStats[statsField],

          editor: {
            type: "bool"
          }
        });
      }
    }

    private _addSequenceStats() {
      let formBuilder = this._formBuilder;

      let statSetOptions = this._statSetOptions;

      formBuilder.addEntry({
        isGroupHeader: true,
        name: "returnSequencesGroup",
        label: "Additional Return Sequence Stats"
      });

      formBuilder.addEntry({
        name: "sequences",
        label: "Sequences",

        help: {
          html: "<p>Custom return sequences allow for analysis of alternate return sequences (other than the standard monthly returns).</p>"
        },

        initialValue: statSetOptions.sequences,

        createEditor: (createEditorOptions) => {
          let sequenceListBuilder = new Solovis.UI.VerticalOrderedListBuilder({
            controlHost: createEditorOptions.container,

            itemTypeLabel: "Sequence",
            showEditLinks: true,

            values: createEditorOptions.initialValue,

            editorCreator: (editorDiv, initialValue) => {
              let sequenceEntryControl = new SequenceEntryControl({
                controlHost: editorDiv,
                sequence: initialValue
              });
              sequenceEntryControl.setAsCurrentControl();

              let editorInfo = {
                getValue: function () {
                  let sequence = sequenceEntryControl.getSequence();
                  return sequence;
                },

                showEditor: function () {
                  sequenceEntryControl.showEditDialog();
                }
              };

              return editorInfo;
            },

            newValueGenerator: function () {
              return {};
            }
          });
          sequenceListBuilder.setAsCurrentControl();

          let editor = {
            getValue: () => {
              return sequenceListBuilder.getValues();
            }
          };

          return editor;
        },

        applyStandardTextAlignTopPadding: true
      });
    }

    private _addPeaksAndTroughs() {
      let statSetOptions = this._statSetOptions;
      let peaksAndTroughs: StatSetOptionsPeaksAndTroughs = statSetOptions.peaksAndTroughs ? statSetOptions.peaksAndTroughs : {};

      let formBuilder = this._formBuilder;

      formBuilder.addEntry({
        isGroupHeader: true,
        name: "peaksAndTroughsGroup",
        label: "Peaks And Troughs"
      });

      formBuilder.addEntry({
        name: "maxDrawdown",
        label: "Max Drawdown",

        help: { url: "~/Help/InvestmentModelHelp/CalculationsReference#max_drawdown" },

        initialValue: !!peaksAndTroughs.maxDrawdown,

        editor: {
          type: "bool"
        }
      });

      formBuilder.addEntry({
        name: "sincePeakPerformance",
        label: "Since Peak Performance",

        help: { url: "~/Help/InvestmentModelHelp/CalculationsReference#since_peak_performance" },

        initialValue: !!peaksAndTroughs.sincePeakPerformance,

        editor: {
          type: "bool"
        }
      });

      formBuilder.addEntry({
        name: "useQuarterEndsForPeaksAndTroughs",
        label: "Use Quarter Ends For Peaks And Troughs",

        help: "Only consider quarter end dates as possible peaks or troughs.",

        initialValue: !!peaksAndTroughs.useQuarterEndsForPeaksAndTroughs,

        editor: {
          type: "bool"
        }
      });
    }

    private _addBetaAlpha() {
      let statSetOptions = this._statSetOptions;

      let formBuilder = this._formBuilder;

      formBuilder.addEntry({
        isGroupHeader: true,
        name: "betaAlphaGroup",
        label: "Beta/Alpha"
      });

      formBuilder.addEntry({
        name: "betaAlpha",
        label: "Beta/Alpha",

        help: { url: "~/Help/InvestmentModelHelp/CalculationsReference#beta_alpha" },

        initialValue: !!statSetOptions.betaAlpha,

        editor: {
          type: "bool"
        }
      });

      this._addBenchmarkRefEntry(
        "betaAlpha.marketRef", "Beta/Alpha Market Benchmark", statSetOptions.betaAlpha ? statSetOptions.betaAlpha.marketRef : null,
        {
          noRiskFreeRate: true,
          noFixedRate: true,
          isVisibleParent: "betaAlpha"
        });

      formBuilder.addEntry({
        name: "betaAlpha.annualizedAlpha",
        label: "Annualized Alpha",

        help: { url: "~/Help/InvestmentModelHelp/CalculationsReference#annualized_alpha" },

        initialValue: statSetOptions.betaAlpha ? !!statSetOptions.betaAlpha.annualizedAlpha : false,

        editor: {
          type: "bool"
        },

        isVisible: (entry) => { return !!this._formBuilder.getValueByName("betaAlpha"); }
      });

      formBuilder.addEntry({
        name: "betaAlpha.annualizedTreynorRatio",
        label: "Annualized Treynor Ratio",

        help: { url: "~/Help/InvestmentModelHelp/CalculationsReference#annualized_treynor_ratio" },

        initialValue: statSetOptions.betaAlpha ? !!statSetOptions.betaAlpha.annualizedTreynorRatio : false,

        editor: {
          type: "bool"
        },

        isVisible: (entry) => { return !!this._formBuilder.getValueByName("betaAlpha"); }
      });
    }

    private _addSortinoRatio() {
      let statSetOptions = this._statSetOptions;

      let formBuilder = this._formBuilder;

      formBuilder.addEntry({
        isGroupHeader: true,
        name: "sortinoRatioGroup",
        label: "Sortino Ratio"
      });

      formBuilder.addEntry({
        name: "sortinoRatio",
        label: "Sortino Ratio",

        help: { url: "~/Help/InvestmentModelHelp/CalculationsReference#sortino_ratio" },

        initialValue: !!statSetOptions.sortinoRatio,

        editor: {
          type: "bool"
        }
      });

      this._addBenchmarkRefEntry(
        "sortinoRatio.targetReturnRef", "Sortino Ratio Target Return", statSetOptions.sortinoRatio ? statSetOptions.sortinoRatio.targetReturnRef : null,
        {
          isVisibleParent: "sortinoRatio"
        });
    }

    private _addSharpeRatio() {
      let statSetOptions = this._statSetOptions;

      let formBuilder = this._formBuilder;

      formBuilder.addEntry({
        isGroupHeader: true,
        name: "sharpeRatioGroup",
        label: "Sharpe Ratio"
      });

      formBuilder.addEntry({
        name: "sharpeRatio",
        label: "Sharpe Ratio",

        help: { url: "~/Help/InvestmentModelHelp/CalculationsReference#sharpe_ratio" },

        initialValue: !!statSetOptions.sharpeRatio,

        editor: {
          type: "bool"
        }
      });

      this._addBenchmarkRefEntry(
        "sharpeRatio.benchmarkRef", "Sharpe Ratio Benchmark", statSetOptions.sharpeRatio ? statSetOptions.sharpeRatio.benchmarkRef : null,
        {
          isVisibleParent: "sharpeRatio"
        });
    }

    private _addInformationRatio() {
      let statSetOptions = this._statSetOptions;

      let formBuilder = this._formBuilder;

      formBuilder.addEntry({
        isGroupHeader: true,
        name: "informationRatioGroup",
        label: "Information Ratio"
      });

      formBuilder.addEntry({
        name: "informationRatio",
        label: "Information Ratio",

        help: { url: "~/Help/InvestmentModelHelp/CalculationsReference#information_ratio" },

        initialValue: !!statSetOptions.informationRatio,

        editor: {
          type: "bool"
        }
      });

      this._addBenchmarkRefEntry(
        "informationRatio.benchmarkRef", "Information Ratio Benchmark", statSetOptions.informationRatio ? statSetOptions.informationRatio.benchmarkRef : null,
        {
          isVisibleParent: "informationRatio"
        });
    }

    private _addUpsideDownsideCaptureRatio() {
      let statSetOptions = this._statSetOptions;

      let formBuilder = this._formBuilder;

      formBuilder.addEntry({
        isGroupHeader: true,
        name: "upsideDownsideCaptureRatioGroup",
        label: "Upside/Downside Capture Ratio"
      });

      formBuilder.addEntry({
        name: "upsideDownsideCaptureRatio",
        label: "Upside/Downside Capture Ratio",

        help: { url: "~/Help/InvestmentModelHelp/CalculationsReference#upside_downside_capture_ratio" },

        initialValue: !!statSetOptions.upsideDownsideCaptureRatio,

        editor: {
          type: "bool"
        }
      });

      this._addBenchmarkRefEntry(
        "upsideDownsideCaptureRatio.benchmarkRef",
        "Upside/Downside Capture Ratio Benchmark",
        statSetOptions.upsideDownsideCaptureRatio ? statSetOptions.upsideDownsideCaptureRatio.benchmarkRef : null,
        {
          isVisibleParent: "upsideDownsideCaptureRatio"
        });
    }

    private _addPme() {
      let statSetOptions = this._statSetOptions;

      let formBuilder = this._formBuilder;

      formBuilder.addEntry({
        isGroupHeader: true,
        name: "pmeGroup",
        label: "Public Market Equivalent"
      });

      formBuilder.addEntry({
        name: "pme",
        label: "PME",

        help: { url: "~/Help/InvestmentModelHelp/CalculationsReference_PME" },

        initialValue: !!statSetOptions.pme,

        editor: {
          type: "bool"
        }
      });

      this._addBenchmarkRefEntry(
        "pme.benchmarkRef",
        "PME Benchmark",
        statSetOptions.pme ? statSetOptions.pme.benchmarkRef : null,
        {
          isVisibleParent: "pme"
        });

      formBuilder.addEntry({
        name: "pme.longNickelsPme",
        label: "Long Nickels PME",

        help: { url: "~/Help/InvestmentModelHelp/CalculationsReference_PME#long_nickels" },

        initialValue: statSetOptions.pme ? !!statSetOptions.pme.longNickelsPme : false,

        editor: {
          type: "bool"
        },

        isVisible: (entry) => { return !!this._formBuilder.getValueByName("pme"); }
      });

      formBuilder.addEntry({
        name: "pme.longNickelsPmeGainLoss",
        label: "Long Nickels PME Gain/Loss",

        help: { url: "~/Help/InvestmentModelHelp/CalculationsReference_PME#long_nickels_gain_loss" },

        initialValue: statSetOptions.pme ? !!statSetOptions.pme.longNickelsPmeGainLoss : false,

        editor: {
          type: "bool"
        },

        isVisible: (entry) => { return !!this._formBuilder.getValueByName("pme") && !!this._formBuilder.getValueByName("pme.longNickelsPme"); }
      });

      formBuilder.addEntry({
        name: "pme.pmePlus",
        label: "PME+",

        help: { url: "~/Help/InvestmentModelHelp/CalculationsReference_PME#pme_plus" },

        initialValue: statSetOptions.pme ? !!statSetOptions.pme.pmePlus : false,

        editor: {
          type: "bool"
        },

        isVisible: (entry) => { return !!this._formBuilder.getValueByName("pme"); }
      });

      formBuilder.addEntry({
        name: "pme.modifiedPme",
        label: "Modified PME",

        help: { url: "~/Help/InvestmentModelHelp/CalculationsReference_PME#modified_pme" },

        initialValue: statSetOptions.pme ? !!statSetOptions.pme.modifiedPme : false,

        editor: {
          type: "bool"
        },

        isVisible: (entry) => { return !!this._formBuilder.getValueByName("pme"); }
      });

      formBuilder.addEntry({
        name: "pme.kaplanSchoarPme",
        label: "Kaplan Schoar PME",

        help: { url: "~/Help/InvestmentModelHelp/CalculationsReference_PME#ks_pme" },

        initialValue: statSetOptions.pme ? !!statSetOptions.pme.kaplanSchoarPme : false,

        editor: {
          type: "bool"
        },

        isVisible: (entry) => { return !!this._formBuilder.getValueByName("pme"); }
      });

      formBuilder.addEntry({
        name: "pme.directAlpha",
        label: "Direct Alpha",

        help: { url: "~/Help/InvestmentModelHelp/CalculationsReference_PME#direct_alpha" },

        initialValue: statSetOptions.pme ? !!statSetOptions.pme.directAlpha : false,

        editor: {
          type: "bool"
        },

        isVisible: (entry) => { return !!this._formBuilder.getValueByName("pme"); }
      });

      formBuilder.addEntry({
        name: "pme.excessIrr",
        label: "Excess IRR",

        help: { url: "~/Help/InvestmentModelHelp/CalculationsReference_PME#excess_irr" },

        initialValue: statSetOptions.pme ? !!statSetOptions.pme.excessIrr : false,

        editor: {
          type: "bool"
        },

        isVisible: (entry) => { return !!this._formBuilder.getValueByName("pme"); }
      });
    }

    private _addComposite() {
      let statSetOptions = this._statSetOptions;
      let composite = statSetOptions.composite;
      let attribution = composite ? composite.attribution : null;

      let formBuilder = this._formBuilder;


      let createAssetListBuilder = (container, parameterType: "entity" | "investment", itemTypeLabel: string, initialValue: any) => {
        let itemValues = _.map(initialValue, (id) => {
          if (parameterType === "entity") {
            return { entityId: id };
          }
          else if (parameterType === "investment") {
            return { investmentId: id };
          }
        });

        let assetListBuilder = new Solovis.UI.VerticalOrderedListBuilder({
          controlHost: container,

          itemTypeLabel: itemTypeLabel,
          showEditLinks: false,

          values: itemValues,

          editorCreator: (editorDiv, initialValue) => {
            let assetEditor = new Solovis.UI.InputControls.ParameterInputControl({
              controlHost: editorDiv,
              parameterType: parameterType,
              value: initialValue
            });
            assetEditor.setAsCurrentControl();

            let editorInfo = {
              getValue: function () {
                let asset = assetEditor.getValue();

                if (parameterType === "entity") {
                  return asset.entityId;
                }
                else if (parameterType === "investment") {
                  return asset.investmentId;
                }
              }
            };

            return editorInfo;
          },

          newValueGenerator: function () {
            return null;
          }
        });
        assetListBuilder.setAsCurrentControl();

        return assetListBuilder;
      };


      formBuilder.addEntry({
        isGroupHeader: true,
        name: "compositeGroup",
        label: "Composite"
      });

      formBuilder.addEntry({
        name: "composite.contributionToReturn",
        label: "Contribution To Return",

        help: { url: "~/Help/InvestmentModelHelp/CalculationsReference_Composites#contr_to_return" },

        initialValue: statSetOptions.composite && statSetOptions.composite.contributionToReturn,

        editor: {
          type: "bool"
        }
      });

      formBuilder.addEntry({
        name: "composite.percentOfAll",
        label: "% Of All Value",

        help: "Ending value for the interval divided by the ending value of the 'All' group.",

        initialValue: statSetOptions.composite && statSetOptions.composite.percentOfAll,

        editor: {
          type: "bool"
        }
      });

      formBuilder.addEntry({
        isGroupHeader: true,
        name: "attributionGroup",
        label: "Attribution"
      });

      formBuilder.addEntry({
        name: "composite.attribution",
        label: "Attribution",

        help: { url: "~/Help/InvestmentModelHelp/CalculationsReference_Attribution" },

        initialValue: !!attribution,

        editor: {
          type: "bool"
        }
      });

      let attrStyleModes = [
        { text: "Arithmetic", value: "Arithmetic" },
        { text: "Geometric", value: "Geometric" }
      ];

      let arithmeticAttrHelpUrl = Solovis.resolveUrl("~/Help/InvestmentModelHelp/CalculationsReference_Attribution");
      let geometricAttrHelpUrl = Solovis.resolveUrl("~/Help/InvestmentModelHelp/CalculationsReference_GeometricAttribution");

      formBuilder.addEntry({
        name: "composite.attribution.style",
        label: "Style",

        help: {
          html: "<p><a href='" + arithmeticAttrHelpUrl + "' target='_blank'>Arithmetic attribution</a> represents active return as the sum of various effects: effect_1 + ... + effect_n.</p>"
            + "<p><a href='" + geometricAttrHelpUrl + "' target='_blank'>Geometric attribution</a> represents active return as the chaining of effects: (1 + effect_1) * ... * (1 + effect_n) - 1.</p>"
        },

        initialValue: (attribution && attribution.style) ? attribution.style : "Arithmetic",

        editor: {
          type: "select",
          selectOptions: {
            data: attrStyleModes
          }
        },

        isVisible: (entry) => { return !!this._formBuilder.getValueByName("composite.attribution"); }
      });

      formBuilder.addEntry({
        name: "composite.attribution.benchmarkEntityIds",
        label: "Attribution Benchmark Tiers",

        help: { url: "~/Help/InvestmentModelHelp/CalculationsReference_Attribution#bm_tiers" },

        initialValue: attribution ? attribution.benchmarkEntityIds : null,

        createEditor: (createEditorOptions) => {
          let initialEntities = _.map(createEditorOptions.initialValue, (entityId) => { return { entityId: entityId }; });

          let entityListBuilder = createAssetListBuilder(createEditorOptions.container, "entity", "Entity", createEditorOptions.initialValue);

          let editor = {
            getValue: () => {
              return entityListBuilder.getValues();
            }
          };

          return editor;
        },

        isVisible: (entry) => { return !!this._formBuilder.getValueByName("composite.attribution"); }
      });

      let selectionEffectModes = [
        { text: "With Selection Effect", value: "WithSelectionEffect" },
        { text: "With Allocation Effect", value: "WithAllocationEffect" },
        { text: "Standalone", value: "Standalone" }
      ];

      formBuilder.addEntry({
        name: "composite.attribution.interactionEffect",
        label: "Interaction Effect",

        help: { url: "~/Help/InvestmentModelHelp/CalculationsReference_Attribution#interaction_effect" },

        initialValue: (attribution && attribution.interactionEffect) ? attribution.interactionEffect : "WithSelectionEffect",

        editor: {
          type: "select",
          selectOptions: {
            data: selectionEffectModes
          }
        },

        isVisible: (entry) => {
          return !!this._formBuilder.getValueByName("composite.attribution") && this._formBuilder.getValueByName("composite.attribution.style") === "Arithmetic";
        }
      });

      formBuilder.addEntry({
        name: "composite.attribution.includeLineItemBenchmarkEffect",
        label: "Include Line Item Benchmark Effect",

        help: { url: "~/Help/InvestmentModelHelp/CalculationsReference_Attribution#line_item_bm_effect" },

        initialValue: (attribution && attribution.includeLineItemBenchmarkEffect) ? true : false,

        editor: {
          type: "bool"
        },

        isVisible: (entry) => {
          return !!this._formBuilder.getValueByName("composite.attribution") && this._formBuilder.getValueByName("composite.attribution.style") === "Arithmetic";
        }
      });
    }


    private _addTx() {
      let statSetOptions = this._statSetOptions;
      let tx: StatSetOptionsTx = statSetOptions.tx ? statSetOptions.tx : {};
      let txPreviouslySet = !!statSetOptions.tx;

      let formBuilder = this._formBuilder;

      formBuilder.addEntry({
        isGroupHeader: true,
        name: "txGroup",
        label: "Transactions"
      });

      formBuilder.addEntry({
        name: "includeTx",
        label: "Include Transaction Summaries",

        help: "Transaction totals will only include external transactions dated from the day after interval 'From' day up to and including the 'To' date.",

        initialValue: txPreviouslySet,

        editor: {
          type: "bool"
        }
      });

      formBuilder.addEntry({
        name: "tx.netAmountIn",
        label: "Net Amount In",

        initialValue: txPreviouslySet ? !!tx.netAmountIn : true,

        editor: {
          type: "bool"
        },

        isVisible: (entry) => { return !!this._formBuilder.getValueByName("includeTx"); }
      });

      formBuilder.addEntry({
        name: "tx.amountIn",
        label: "Amount In",

        initialValue: !!tx.amountIn,

        editor: {
          type: "bool"
        },

        isVisible: (entry) => { return !!this._formBuilder.getValueByName("includeTx"); }
      });

      formBuilder.addEntry({
        name: "tx.amountOut",
        label: "Amount Out",

        initialValue: !!tx.amountOut,

        editor: {
          type: "bool"
        },

        isVisible: (entry) => { return !!this._formBuilder.getValueByName("includeTx"); }
      });

      formBuilder.addEntry({
        name: "tx.dietzWeightedIn",
        label: "Dietz Weighted In",

        help: "This will retrieve net weighted cashflows in for Modified Dietz.",

        initialValue: !!tx.dietzWeightedIn,

        editor: {
          type: "bool"
        },

        isVisible: (entry) => { return !!this._formBuilder.getValueByName("includeTx"); }
      });

      formBuilder.addEntry({
        name: "tx.netIncomeIn",
        label: "Net Income In",

        help: "This will retrieve net accrued income bought and sold during the interval.",

        initialValue: !!tx.netIncomeIn,

        editor: {
          type: "bool"
        },

        isVisible: (entry) => { return !!this._formBuilder.getValueByName("includeTx"); }
      });

      formBuilder.addEntry({
        name: "tx.localValues",
        label: "Local Values",

        help: "Include transaction summary values in local currency of line item in addition to the target currency.",

        initialValue: !!tx.localValues,

        editor: {
          type: "bool"
        },

        isVisible: (entry) => { return !!this._formBuilder.getValueByName("includeTx"); }
      });

      formBuilder.addEntry({
        name: "tx.customTxGroups",
        label: "Custom Groups",

        initialValue: tx.customTxGroups,

        createEditor: (createEditorOptions) => {
          let groupListBuilder = new Solovis.UI.VerticalOrderedListBuilder({
            controlHost: createEditorOptions.container,

            itemTypeLabel: "Grouping",
            showEditLinks: true,

            values: createEditorOptions.initialValue,

            editorCreator: (editorDiv, initialValue) => {
              let lineItemState = {
                customTxGroup: initialValue
              };

              let updateEditorDiv = () => {
                if (lineItemState.customTxGroup && lineItemState.customTxGroup.label) {
                  editorDiv.text(lineItemState.customTxGroup.label);
                }
                else {
                  editorDiv.text("(Unnamed Group)");
                }
              };

              updateEditorDiv();

              let editorInfo = {
                getValue: () => {
                  return lineItemState.customTxGroup;
                },

                showEditor: () => {
                  CustomTxGroupControl.showEditDialog(
                    lineItemState.customTxGroup,
                    (newCustomTxGroup) => {
                      lineItemState.customTxGroup = newCustomTxGroup;
                      updateEditorDiv();
                    });
                }
              };

              return editorInfo;
            },

            newValueGenerator: () => {
              let itemCount = groupListBuilder.getItemCount();
              let nextCount = 1;

              // Make sure we don't duplicate group code.
              if (itemCount > 0) {
                let currentItems = groupListBuilder.getValues();
                let isCountUsable = (possibleCount) => {
                  for (let item of currentItems) {
                    if (item.code === ("group" + possibleCount)) {
                      return false;
                    }
                  }

                  return true;
                };

                while (!isCountUsable(nextCount)) {
                  nextCount++
                }
              }

              let newGroup = {
                label: "Group " + nextCount,
                code: "group" + nextCount
              };

              return newGroup;
            }
          });
          groupListBuilder.setAsCurrentControl();

          let editor = {
            getValue: () => {
              return groupListBuilder.getValues();
            }
          };

          return editor;
        },

        isVisible: (entry) => { return !!this._formBuilder.getValueByName("includeTx"); }
      });

      formBuilder.addEntry({
        name: "tx.usePartialPeriodLogic",
        label: "Use Partial Period Logic",
        help: "When Partial Period logic is checked off, users can pull transaction summaries over specific dates within the selected intervals defined by using the Start and End Date properties below.",
        initialValue: statSetOptions.tx
          ? statSetOptions.tx.usePartialPeriodLogic
          : false,
        editor: {
          type: "bool"
        },
        isVisible: (entry) => { return !!this._formBuilder.getValueByName("includeTx"); }
      });

      var resourceTypeIds = [InvData.ResourceTypeIds.Entity, InvData.ResourceTypeIds.Investment];

      var _filter = {
        propertyTypeId: InvData.PropertyTypeIds.Date,
        resourceTypeIds: resourceTypeIds
      };

      var propertyDefinitionDataSource: Solovis.UI.InputControls.FormEditorSimpleDataSource = {
        load: function (loadOptions) {
          let promise = new Promise(function (resolve, reject) {
            if (this._propertyList) {
              resolve(this._propertyList);
            }
            else {
              Solovis.Rpc.invoke(
                "~/Selectors/PropertySelector/GetPropertyDefinitions",
                { filter: _filter },
                function (response, success) {
                  if (success) {
                    let propertyList = _.map(response.result.modelEnvironment.propertyDefinitions, function (property: any) {
                      return {
                        text: property.label,
                        value: property.id
                      };
                    }).sort((a, b) => (a.text >= b.text ? 1 : -1));
                    this._propertyList = propertyList;
                    resolve(propertyList);
                  }
                  else {
                    reject(response);
                  }
                });
            }
          });

          return promise;
        },
        byKey: function (key) {
          let promise = new Promise(function (resolve, reject) {
            if (this._propertyList) {
              var propertyData = this._propertyList.filter(p => p.value === key);
              resolve(propertyData);
            }
            else {
              Solovis.Rpc.invoke(
                "~/Selectors/PropertySelector/GetPropertyData",
                { id: key },
                function (response, success) {
                  if (success) {
                    let property = response.result;
                    if (property) {
                      let propertyData = {
                        text: property.label,
                        value: property.id
                      };
                      resolve(propertyData);
                    }
                    else {
                      resolve(null);
                    }
                  }
                  else {
                    reject(response);
                  }
                });
            }
          });

          return promise;
        }
      };

      formBuilder.addEntry({
        name: "tx.chooseStartDateProperty",
        label: "Choose Start Date Property",
        help: "Allows users to specify a Start Date for custom transaction summaries.",
        initialValue: statSetOptions.tx
          ? statSetOptions.tx.startDateResourceId
          : "0",
        editor: {
          type: "select",
          selectOptions: {
            dataSource: propertyDefinitionDataSource,
            showClearButton: true
          }
        },
        isVisible: (entry) => { return (!!this._formBuilder.getValueByName("tx.usePartialPeriodLogic") && !!this._formBuilder.getValueByName("includeTx")); }
      });

      formBuilder.addEntry({
        name: "tx.chooseEndDateProperty",
        label: "Choose End Date Property",
        help: "Allows users to specify an End Date for custom transaction summaries.",
        initialValue: statSetOptions.tx
          ? statSetOptions.tx.endDateResourceId
          : "0",
        editor: {
          type: "select",
          selectOptions: {
            dataSource: propertyDefinitionDataSource,
            showClearButton: true
          }
        },
        isVisible: (entry) => { return (!!this._formBuilder.getValueByName("tx.usePartialPeriodLogic") && !!this._formBuilder.getValueByName("includeTx")); }
      });
    }

    private _addDrawdown() {
      let statSetOptions = this._statSetOptions;
      let drawdown: StatSetOptionsDrawdown = statSetOptions.drawdown ? statSetOptions.drawdown : { end: true, uncalledCapital: true };
      let drawdownPreviouslySet = !!statSetOptions.drawdown;

      let formBuilder = this._formBuilder;

      formBuilder.addEntry({
        isGroupHeader: true,
        name: "drawdownGroup",
        label: "Drawdown"
      });

      formBuilder.addEntry({
        name: "includeDrawdown",
        label: "Include Drawdown Stats",

        initialValue: drawdownPreviouslySet,

        help: {
          html: "<p>IMPORTANT: All drawdown stats in Interval Statistics are based on the final set of assets in the group"
            + " at the end of each interval. This is different from all other calculations that are based on the synthetic"
            + " composite line item that factors in time-slicing and weight changes within the interval.</p>"
            + " <p>Any line items that contain a weighting other than 0% or 100% for any component at the end of"
            + " the interval will show N/V's for drawdown stats values.</p>"
        },

        editor: {
          type: "bool"
        }
      });

      formBuilder.addEntry({
        name: "drawdown.end",
        label: "Ending Values",

        initialValue: drawdown.end,

        editor: {
          type: "bool"
        },

        isVisible: (entry) => { return !!this._formBuilder.getValueByName("includeDrawdown"); }
      });

      formBuilder.addEntry({
        name: "drawdown.start",
        label: "Starting Values",

        initialValue: drawdown.start,

        editor: {
          type: "bool"
        },

        isVisible: (entry) => { return !!this._formBuilder.getValueByName("includeDrawdown"); }
      });

      formBuilder.addEntry({
        name: "drawdown.local",
        label: "Local Values",

        initialValue: drawdown.local,

        editor: {
          type: "bool"
        },

        isVisible: (entry) => { return !!this._formBuilder.getValueByName("includeDrawdown"); }
      });

      formBuilder.addEntry({
        name: "drawdown.commitment",
        label: "Commitment",

        initialValue: drawdown.commitment,

        editor: {
          type: "bool"
        },

        isVisible: (entry) => { return !!this._formBuilder.getValueByName("includeDrawdown"); }
      });

      formBuilder.addEntry({
        name: "drawdown.uncalledCapital",
        label: "Uncalled Capital",

        initialValue: drawdown.uncalledCapital,

        editor: {
          type: "bool"
        },

        isVisible: (entry) => { return !!this._formBuilder.getValueByName("includeDrawdown"); }
      });

      formBuilder.addEntry({
        name: "drawdown.recallable",
        label: "Recallable",

        initialValue: drawdown.recallable,

        editor: {
          type: "bool"
        },

        isVisible: (entry) => { return !!this._formBuilder.getValueByName("includeDrawdown"); }
      });

      formBuilder.addEntry({
        name: "drawdown.tvpi",
        label: "TVPI Since Inception",

        initialValue: drawdown.tvpi,

        editor: {
          type: "bool"
        },

        isVisible: (entry) => { return !!this._formBuilder.getValueByName("includeDrawdown"); }
      });

      formBuilder.addEntry({
        name: "drawdown.dpi",
        label: "DPI Since Inception",

        initialValue: drawdown.dpi,

        editor: {
          type: "bool"
        },

        isVisible: (entry) => { return !!this._formBuilder.getValueByName("includeDrawdown"); }
      });

      formBuilder.addEntry({
        name: "drawdown.capitalCalledSinceInception",
        label: "Capital Called Since Inception",

        initialValue: drawdown.capitalCalledSinceInception,

        editor: {
          type: "bool"
        },

        isVisible: (entry) => { return !!this._formBuilder.getValueByName("includeDrawdown"); }
      });

      formBuilder.addEntry({
        name: "drawdown.distributionsSinceInception",
        label: "Distributions Since Inception",

        initialValue: drawdown.distributionsSinceInception,

        editor: {
          type: "bool"
        },

        isVisible: (entry) => { return !!this._formBuilder.getValueByName("includeDrawdown"); }
      });
    }

    private _addAdvancedSection() {
      let statSetOptions = this._statSetOptions;
      let basicStats: StatSetOptionsBasicStats = statSetOptions.basicStats ? statSetOptions.basicStats : {};

      let formBuilder = this._formBuilder;

      // Basic Stats
      formBuilder.addEntry({
        isGroupHeader: true,
        name: "advanced",
        label: "Advanced"
      });

      let defaultBenchmarkOptions = [];
      defaultBenchmarkOptions.push({ text: "Default", value: "Default" });
      defaultBenchmarkOptions.push({ text: "Primary", value: "Primary" });
      defaultBenchmarkOptions.push({ text: "Secondary", value: "Secondary" });

      formBuilder.addEntry({
        name: "advanced.defaultBenchmark.source",
        label: "Default Benchmark",

        initialValue: (statSetOptions.defaultBenchmark && statSetOptions.defaultBenchmark.source) ? statSetOptions.defaultBenchmark.source : "Default",

        editor: {
          type: "select",
          selectOptions: {
            data: defaultBenchmarkOptions
          }
        }
      });

      formBuilder.addEntry({
        name: "advanced.endOfDayCumulativeIrr",
        label: "EoD Cumulative IRR",

        help: { url: "~/Help/InvestmentModelHelp/CalculationsReference#eod_cumulative_irr" },

        initialValue: basicStats["endOfDayCumulativeIrr"],

        editor: {
          type: "bool"
        }
      });

      formBuilder.addEntry({
        name: "advanced.endOfDayAnnualizedIrr",
        label: "EoD Annualized IRR",

        help: { url: "~/Help/InvestmentModelHelp/CalculationsReference#eod_annualized_irr" },

        initialValue: basicStats["endOfDayAnnualizedIrr"],

        editor: {
          type: "bool"
        }
      });

      formBuilder.addEntry({
        name: "advanced.hidePartialPeriodReturns",
        label: "Hide Partial Period Returns",

        help: {
          html: "<p>If Hide Partial Period Returns is selected, investments that incepted after the beginning of the period"
            + " in question or were liquidated prior to period end will only show if returns existed for the entire period.</p>"
        },

        initialValue: basicStats["hidePartialPeriodReturns"],

        editor: {
          type: "bool"
        }
      });
    }




    private _getNewStatSetOptions() {
      let newOptions: StatSetOptions = {};

      let riskFreeRateRef = this._formBuilder.getValueByName("riskFreeRateRef");
      if (riskFreeRateRef) {
        newOptions.riskFreeRateRef = riskFreeRateRef;
      }

      // basicStats
      let basicStats: StatSetOptionsBasicStats = {};
      for (let statsField of StatSetEditorDialog._coreStatsFieldList) {
        let isStatEnabled = this._formBuilder.getValueByName(statsField);
        if (isStatEnabled) {
          basicStats[statsField] = true;
        }
      }

      let defaultBenchmarkSource = this._formBuilder.getValueByName("advanced.defaultBenchmark.source");
      if (defaultBenchmarkSource && defaultBenchmarkSource !== "Default") {
        newOptions.defaultBenchmark = { source: defaultBenchmarkSource };
      }

      let annualizationMethod = this._formBuilder.getValueByName("annualizationMethod");
      if (annualizationMethod) {
        newOptions.annualizationMethod = annualizationMethod;
      }

      let endOfDayCumulativeIrr = this._formBuilder.getValueByName("advanced.endOfDayCumulativeIrr");
      if (endOfDayCumulativeIrr) {
        basicStats.endOfDayCumulativeIrr = true;
      }
      let endOfDayAnnualizedIrr = this._formBuilder.getValueByName("advanced.endOfDayAnnualizedIrr");
      if (endOfDayAnnualizedIrr) {
        basicStats.endOfDayAnnualizedIrr = true;
      }

      let hidePartialPeriodReturns = this._formBuilder.getValueByName("advanced.hidePartialPeriodReturns");
      basicStats.hidePartialPeriodReturns = hidePartialPeriodReturns;

      let twrVariations = this._formBuilder.getValueByName("twrVariations");
      if (!_.isEmpty(twrVariations)) {
        basicStats.twrVariations = twrVariations;
      }

      let irrVariations = this._formBuilder.getValueByName("irrVariations");
      if (!_.isEmpty(irrVariations)) {
        basicStats.irrVariations = irrVariations;
      }

      newOptions.basicStats = basicStats;

      // benchmarkedBasicStats
      let basicStatsBenchmarkRef = this._formBuilder.getValueByName("basicStatsBenchmarkRef");
      if (basicStatsBenchmarkRef) {
        newOptions.basicStatsBenchmarkRef = basicStatsBenchmarkRef;
      }

      let benchmarkedBasicStats: StatSetOptionsBenchmarkedBasicStats = {};
      for (let statsField of StatSetEditorDialog._benchmarkedStatsFieldList) {
        let isStatEnabled = this._formBuilder.getValueByName(statsField);
        if (isStatEnabled) {
          benchmarkedBasicStats[statsField] = true;
        }
      }

      newOptions.benchmarkedBasicStats = benchmarkedBasicStats;

      // Peaks And Troughs
      let maxDrawdown = this._formBuilder.getValueByName("maxDrawdown");
      let sincePeakPerformance = this._formBuilder.getValueByName("sincePeakPerformance");
      let useQuarterEndsForPeaksAndTroughs = this._formBuilder.getValueByName("useQuarterEndsForPeaksAndTroughs");
      if (maxDrawdown || sincePeakPerformance || useQuarterEndsForPeaksAndTroughs) {
        let peaksAndTroughs: StatSetOptionsPeaksAndTroughs = {};
        if (maxDrawdown) {
          peaksAndTroughs.maxDrawdown = true;
        }
        if (sincePeakPerformance) {
          peaksAndTroughs.sincePeakPerformance = true;
        }
        if (useQuarterEndsForPeaksAndTroughs) {
          peaksAndTroughs.useQuarterEndsForPeaksAndTroughs = true;
        }
        newOptions.peaksAndTroughs = peaksAndTroughs;
      }

      let gainLossSummary = this._formBuilder.getValueByName("gainLossSummary");
      if (gainLossSummary) {
        newOptions.gainLossSummary = true;
      }

      this._applyValuesGroup(newOptions);
      this._applySequencesToNewOptions(newOptions);
      this._appyBetaAlphaToNewOptions(newOptions);
      this._applySortinoRatioToNewOptions(newOptions);
      this._applySharpeRatioToNewOptions(newOptions);
      this._applyInformationRatioToNewOptions(newOptions);
      this._applyUpsideDownsideCaptureRatioToNewOptions(newOptions);
      this._appyPmeToNewOptions(newOptions);
      this._applyCompositeToOptions(newOptions);
      this._applyTxGroup(newOptions);
      this._applyDrawdownToNewOptions(newOptions);

      return newOptions;
    }

    private _applyValuesGroup(newOptions: StatSetOptions) {
      let values = this._formBuilder.getValueByName("values");
      if (values) {
        let valueOptions: StatSetOptionsValues = {};
        if (this._formBuilder.getValueByName("values.endValue")) {
          valueOptions.endValue = true;
        }
        if (this._formBuilder.getValueByName("values.startValue")) {
          valueOptions.startValue = true;
        }
        if (this._formBuilder.getValueByName("values.units")) {
          valueOptions.units = true;
        }
        if (this._formBuilder.getValueByName("values.unitPrice")) {
          valueOptions.unitPrice = true;
        }
        if (this._formBuilder.getValueByName("values.contractSize")) {
          valueOptions.contractSize = true;
        }
        if (this._formBuilder.getValueByName("values.ownershipPercentage")) {
          valueOptions.ownershipPercentage = true;
        }
        if (this._formBuilder.getValueByName("values.averageBalance")) {
          valueOptions.averageBalance = true;
        }
        if (this._formBuilder.getValueByName("values.localValues")) {
          valueOptions.localValues = true;
        }
        if (this._formBuilder.getValueByName("values.netValueGainLoss")) {
          valueOptions.netValueGainLoss = true;
        }
        if (this._formBuilder.getValueByName("values.income")) {
          valueOptions.income = true;
        }

        let lastValueAboveValueTypeThresholdId = this._formBuilder.getValueByName("values.lastValueAboveValueTypeThresholdId");
        if (lastValueAboveValueTypeThresholdId) {
          valueOptions.lastValueAboveValueTypeThresholdId = lastValueAboveValueTypeThresholdId;
        }

        if (this._formBuilder.getValueByName("values.accruedIncomeStartValue")) {
          valueOptions.accruedIncomeStartValue = true;
        }
        if (this._formBuilder.getValueByName("values.accruedIncomeEndValue")) {
          valueOptions.accruedIncomeEndValue = true;
        }

        let twrVariations = this._formBuilder.getValueByName("values.twrVariations");
        if (!_.isEmpty(twrVariations)) {
          valueOptions.twrVariations = twrVariations;
        }

        newOptions.values = valueOptions;
      }
    }

    private _applySequencesToNewOptions(newOptions: StatSetOptions) {
      let sequences = this._formBuilder.getValueByName("sequences");
      if (!_.isEmpty(sequences)) {
        newOptions.sequences = sequences;
      }
    }

    private _appyBetaAlphaToNewOptions(newOptions: StatSetOptions) {
      let betaAlpha = this._formBuilder.getValueByName("betaAlpha");
      if (betaAlpha) {
        let betaAlphaOptions: StatSetOptionsBetaAlpha = {};
        betaAlphaOptions.marketRef = this._formBuilder.getValueByName("betaAlpha.marketRef");
        if (this._formBuilder.getValueByName("betaAlpha.annualizedAlpha")) {
          betaAlphaOptions.annualizedAlpha = true;
        }
        if (this._formBuilder.getValueByName("betaAlpha.annualizedTreynorRatio")) {
          betaAlphaOptions.annualizedTreynorRatio = true;
        }

        newOptions.betaAlpha = betaAlphaOptions;
      }
    }

    private _applySortinoRatioToNewOptions(newOptions: StatSetOptions) {
      let sortinoRatio = this._formBuilder.getValueByName("sortinoRatio");
      if (sortinoRatio) {
        let sortinoRatioOptions: StatSetOptionsSortinoRatio = {};
        sortinoRatioOptions.targetReturnRef = this._formBuilder.getValueByName("sortinoRatio.targetReturnRef");

        newOptions.sortinoRatio = sortinoRatioOptions;
      }
    }

    private _applySharpeRatioToNewOptions(newOptions: StatSetOptions) {
      let sharpeRatio = this._formBuilder.getValueByName("sharpeRatio");
      if (sharpeRatio) {
        let sharpeRatioOptions: StatSetOptionsSharpeRatio = {};
        sharpeRatioOptions.benchmarkRef = this._formBuilder.getValueByName("sharpeRatio.benchmarkRef");

        newOptions.sharpeRatio = sharpeRatioOptions;
      }
    }

    private _applyInformationRatioToNewOptions(newOptions: StatSetOptions) {
      let informationRatio = this._formBuilder.getValueByName("informationRatio");
      if (informationRatio) {
        let informationRatioOptions: StatSetOptionsInformationRatio = {};
        informationRatioOptions.benchmarkRef = this._formBuilder.getValueByName("informationRatio.benchmarkRef");

        newOptions.informationRatio = informationRatioOptions;
      }
    }

    private _applyUpsideDownsideCaptureRatioToNewOptions(newOptions: StatSetOptions) {
      let upsideDownsideCaptureRatio = this._formBuilder.getValueByName("upsideDownsideCaptureRatio");
      if (upsideDownsideCaptureRatio) {
        let upsideDownsideCaptureRatioOptions: StatSetOptionsUpsideDownsideCaptureRatio = {};
        upsideDownsideCaptureRatioOptions.benchmarkRef = this._formBuilder.getValueByName("upsideDownsideCaptureRatio.benchmarkRef");

        newOptions.upsideDownsideCaptureRatio = upsideDownsideCaptureRatioOptions;
      }
    }

    private _appyPmeToNewOptions(newOptions: StatSetOptions) {
      let pme = this._formBuilder.getValueByName("pme");
      if (pme) {
        let pme: StatSetOptionsPme = {};
        pme.benchmarkRef = this._formBuilder.getValueByName("pme.benchmarkRef");
        if (this._formBuilder.getValueByName("pme.longNickelsPme")) {
          pme.longNickelsPme = true;
        }
        if (this._formBuilder.getValueByName("pme.longNickelsPmeGainLoss")) {
          pme.longNickelsPmeGainLoss = true;
        }
        if (this._formBuilder.getValueByName("pme.pmePlus")) {
          pme.pmePlus = true;
        }
        if (this._formBuilder.getValueByName("pme.modifiedPme")) {
          pme.modifiedPme = true;
        }
        if (this._formBuilder.getValueByName("pme.kaplanSchoarPme")) {
          pme.kaplanSchoarPme = true;
        }
        if (this._formBuilder.getValueByName("pme.directAlpha")) {
          pme.directAlpha = true;
        }
        if (this._formBuilder.getValueByName("pme.excessIrr")) {
          pme.excessIrr = true;
        }

        newOptions.pme = pme;
      }
    }

    private _applyCompositeToOptions(newOptions: StatSetOptions) {
      let contributionToReturn = this._formBuilder.getValueByName("composite.contributionToReturn");
      let percentOfAll = this._formBuilder.getValueByName("composite.percentOfAll");
      let attribution = this._formBuilder.getValueByName("composite.attribution");

      if (contributionToReturn || percentOfAll || attribution) {
        let composite: StatSetOptionsComposite = {};

        if (contributionToReturn) {
          composite.contributionToReturn = contributionToReturn;
        }
        if (percentOfAll) {
          composite.percentOfAll = percentOfAll;
        }
        if (attribution) {
          composite.attribution = {};
          let attrStyle = this._formBuilder.getValueByName("composite.attribution.style");
          composite.attribution.style = attrStyle;
          composite.attribution.benchmarkEntityIds = this._formBuilder.getValueByName("composite.attribution.benchmarkEntityIds");

          if (attrStyle === "Arithmetic") {
            composite.attribution.interactionEffect = this._formBuilder.getValueByName("composite.attribution.interactionEffect");
            composite.attribution.includeLineItemBenchmarkEffect = this._formBuilder.getValueByName("composite.attribution.includeLineItemBenchmarkEffect");
          }
        }

        newOptions.composite = composite;
      }
      else {
        return null;
      }
    }

    private _applyTxGroup(newOptions: StatSetOptions) {
      let includeTx = this._formBuilder.getValueByName("includeTx");
      if (includeTx) {
        let txOptions: StatSetOptionsTx = {};
        if (this._formBuilder.getValueByName("tx.netAmountIn")) {
          txOptions.netAmountIn = true;
        }
        if (this._formBuilder.getValueByName("tx.amountIn")) {
          txOptions.amountIn = true;
        }
        if (this._formBuilder.getValueByName("tx.amountOut")) {
          txOptions.amountOut = true;
        }
        if (this._formBuilder.getValueByName("tx.dietzWeightedIn")) {
          txOptions.dietzWeightedIn = true;
        }

        if (this._formBuilder.getValueByName("tx.netIncomeIn")) {
          txOptions.netIncomeIn = true;
        }

        if (this._formBuilder.getValueByName("tx.localValues")) {
          txOptions.localValues = true;
        }

        let customTxGroups = this._formBuilder.getValueByName("tx.customTxGroups");
        if (!_.isEmpty(customTxGroups)) {
          txOptions.customTxGroups = customTxGroups;
        }

        if (this._formBuilder.getValueByName("tx.usePartialPeriodLogic")) {
          txOptions.usePartialPeriodLogic = true;
        }

        let startDateResourceId = this._formBuilder.getValueByName("tx.chooseStartDateProperty");
        if (startDateResourceId && txOptions.usePartialPeriodLogic) {
          txOptions.startDateResourceId = startDateResourceId;
        }

        let endDateResourceId = this._formBuilder.getValueByName("tx.chooseEndDateProperty");
        if (endDateResourceId && txOptions.usePartialPeriodLogic) {
          txOptions.endDateResourceId = endDateResourceId;
        }

        newOptions.tx = txOptions;
      }
    }


    private _applyDrawdownToNewOptions(newOptions: StatSetOptions) {
      let includeDrawdown = this._formBuilder.getValueByName("includeDrawdown");
      if (includeDrawdown) {
        let drawdown: StatSetOptionsDrawdown = {};
        if (this._formBuilder.getValueByName("drawdown.end")) {
          drawdown.end = true;
        }
        if (this._formBuilder.getValueByName("drawdown.start")) {
          drawdown.start = true;
        }
        if (this._formBuilder.getValueByName("drawdown.local")) {
          drawdown.local = true;
        }
        if (this._formBuilder.getValueByName("drawdown.commitment")) {
          drawdown.commitment = true;
        }
        if (this._formBuilder.getValueByName("drawdown.uncalledCapital")) {
          drawdown.uncalledCapital = true;
        }
        if (this._formBuilder.getValueByName("drawdown.recallable")) {
          drawdown.recallable = true;
        }
        if (this._formBuilder.getValueByName("drawdown.capitalCalledSinceInception")) {
          drawdown.capitalCalledSinceInception = true;
        }
        if (this._formBuilder.getValueByName("drawdown.distributionsSinceInception")) {
          drawdown.distributionsSinceInception = true;
        }
        if (this._formBuilder.getValueByName("drawdown.tvpi")) {
          drawdown.tvpi = true;
        }
        if (this._formBuilder.getValueByName("drawdown.dpi")) {
          drawdown.dpi = true;
        }

        newOptions.drawdown = drawdown;
      }
    }
  }



}